import type { RouteLocationRaw } from 'vue-router';
import type { useAuthStore } from '@/stores/auth';

/**
 * Where to send a freshly-authenticated user. Matches the legacy SignIn flow:
 * lab users → labOrders, everyone else → reservations. The optional
 * `reservationCode` is passed as a query param (the `/orders` route doesn't
 * declare a path param for it, and vue-router 4 drops undeclared params).
 */
export function landingRouteFor(
  auth: ReturnType<typeof useAuthStore>,
  extras: { reservationCode?: string } = {},
): RouteLocationRaw {
  if (auth.isLabUser) {
    return { name: 'labOrders' };
  }
  return extras.reservationCode
    ? { name: 'reservations', query: { reservationCode: extras.reservationCode } }
    : { name: 'reservations' };
}

/** User types the legacy remote-link flow sent to `/orders` rather than `/labOrders`. */
const CLIENT_USER_TYPES = new Set(['Client User', 'Client Administrator']);

/**
 * Where to send a user arriving via a remote-link sign-in. The legacy
 * `remote_link_signin.js` routed strictly by `emsl_user_type_name`:
 * Client User / Client Administrator → orders, everyone else → labOrders.
 * (This differs from `landingRouteFor`, which keys off `isLabUser` and so also
 * treats `organization_id === 1` as a lab user.)
 */
export function remoteLinkLandingRouteFor(
  auth: ReturnType<typeof useAuthStore>,
  extras: { reservationCode?: string } = {},
): RouteLocationRaw {
  const userType = auth.emslUserType;
  if (userType !== null && CLIENT_USER_TYPES.has(userType)) {
    return extras.reservationCode
      ? { name: 'reservations', query: { reservationCode: extras.reservationCode } }
      : { name: 'reservations' };
  }
  return { name: 'labOrders' };
}
